import { useState, useEffect, useCallback, useMemo } from 'react';
import { List, useListContext, CreateButton, TopToolbar, useRedirect, useNotify, useRefresh } from 'react-admin';
import {
  Box, Typography, Stack, Button, TextField, Autocomplete,
  Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, Chip, IconButton, Tooltip, Switch,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
} from '@mui/material';
import AddIcon    from '@mui/icons-material/Add';
import EditIcon   from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { tbl, btn, inputSx, NAVY } from '../styles/ds';
import authFetch, { responseError } from '../utils/authFetch';
import { useFitCount } from '../hooks/useFitCount';
import FitPagination from '../components/FitPagination';

const TYPE_LABEL = {
  key_map:   { label: 'מיפוי מפתח',  bg: '#CCE5FF', color: '#004085' },
  value_map: { label: 'מיפוי ערך',   bg: '#FFF3CD', color: '#856404' },
  match_on:  { label: 'השוואה על',   bg: '#D4EDDA', color: '#155724' },
};

// ── Quick "new rule set" dialog ─────────────────────────────────────────────────
const AddRuleSetDialog = ({ open, onClose, onAdded }) => {
  const notify = useNotify();
  const [products, setProducts]     = useState([]);
  const [name,     setName]         = useState('');
  const [source,   setSource]       = useState(null);
  const [target,   setTarget]       = useState(null);
  const [saving,   setSaving]       = useState(false);

  useEffect(() => {
    if (!open) return;
    authFetch('/products?_start=0&_end=500&_sort=name&_order=ASC')
      .then(r => r.json())
      .then(d => setProducts(Array.isArray(d) ? d.map(p => ({ id: p.id, label: p.name })) : []))
      .catch(() => setProducts([]));
  }, [open]);

  const handleSave = async () => {
    if (!name.trim()) { notify('נא להזין שם לסט', { type: 'warning' }); return; }
    if (!source || !target) { notify('נא לבחור מוצר מקור ומוצר יעד', { type: 'warning' }); return; }
    setSaving(true);
    try {
      const res = await authFetch('/matching_rule_sets', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), source_product_id: source.id, target_product_id: target.id }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'שגיאה ביצירת הסט');
      notify(`הסט "${body.name}" נוצר`, { type: 'success' });
      onAdded(body);
      setName(''); setSource(null); setTarget(null);
      onClose();
    } catch (e) { notify(e.message, { type: 'error' }); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} dir="rtl" maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: NAVY }}>סט כללי התאמה חדש</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} pt={1}>
          <TextField label="שם הסט" value={name} onChange={e => setName(e.target.value)}
            fullWidth sx={inputSx} autoFocus placeholder='לדוגמה: "מזוזה מוכנה ← קלף מזוזה"' />
          <Autocomplete options={products} value={source} onChange={(_, v) => setSource(v)}
            getOptionLabel={o => o?.label ?? ''} isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={p => <TextField {...p} label="מוצר מקור" sx={inputSx} />} />
          <Autocomplete options={products} value={target} onChange={(_, v) => setTarget(v)}
            getOptionLabel={o => o?.label ?? ''} isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={p => <TextField {...p} label="מוצר יעד" sx={inputSx} />} />
          <Typography fontSize={12} color="text.secondary">
            כשבוחרים וריאנט ממוצר המקור בייצור, המערכת תחפש עבורו התאמה אוטומטית בין הוריאנטים של מוצר היעד.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ color: '#666' }}>ביטול</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <AddIcon />}
          sx={{ backgroundColor: NAVY, borderRadius: 2, '&:hover': { backgroundColor: '#162d5a' } }}>
          צור סט
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Toolbar = () => (
  <TopToolbar><CreateButton label="כלל חדש" sx={btn.create} /></TopToolbar>
);

const RuleTable = () => {
  const { data = [], isLoading } = useListContext();
  const redirect = useRedirect();
  const notify   = useNotify();
  const refresh  = useRefresh();
  const [page, setPage] = useState(1);
  const { fitCount, renderCount, containerRef, startIndex } = useFitCount({ fallback: 10, page });
  const pageItems = useMemo(() => {
    return data.slice(startIndex, startIndex + renderCount);
  }, [data, startIndex, renderCount]);
  useEffect(() => { setPage(1); }, [data]);

  const handleDelete = async (id, desc) => {
    if (!window.confirm(`למחוק את הכלל "${desc || id}"?`)) return;
    const res = await authFetch(`/matching_rules/${id}`, { method: 'DELETE' });
    if (res.ok) { notify('הכלל נמחק', { type: 'success' }); refresh(); }
    else notify(await responseError(res, 'שגיאה במחיקה'), { type: 'error' });
  };

  const toggleActive = async (rule) => {
    await authFetch(`/matching_rules/${rule.id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...rule, active: !rule.active }),
    });
    refresh();
  };

  if (isLoading) return null;

  return (
    <>
    <TableContainer component={Box} sx={{ ...tbl.container, border: '1px solid rgba(13,29,62,0.06)' }}>
      <Box className="crm-table-scroll" data-mobile-scroll tabIndex={0} role="region" aria-label="טבלה נגללת"><Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 60 }}>#</TableCell>
            <TableCell>סוג</TableCell>
            <TableCell>מפתח מקור</TableCell>
            <TableCell>ערך מקור</TableCell>
            <TableCell>מפתח יעד</TableCell>
            <TableCell>ערך יעד</TableCell>
            <TableCell>תיאור</TableCell>
            <TableCell align="center">פעיל</TableCell>
            <TableCell align="center">פעולות</TableCell>
          </TableRow>
        </TableHead>
        <TableBody ref={containerRef}>
          {pageItems.map((r, i) => {
            const tm = TYPE_LABEL[r.rule_type] || { label: r.rule_type, bg: '#eee', color: '#555' };
            return (
              <TableRow key={r.id} sx={{ ...tbl.zebraRow(i), opacity: r.active ? 1 : 0.45 }}>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 13 }}>#{r.id}</TableCell>
                <TableCell>
                  <Chip label={tm.label} size="small" sx={{ backgroundColor: tm.bg, color: tm.color, fontWeight: 700, fontSize: 11 }} />
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{r.from_key || <em style={{ color: '#aaa' }}>כל מפתח</em>}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600 }}>{r.from_value || '—'}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{r.to_key || '—'}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600 }}>{r.to_value || '—'}</TableCell>
                <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{r.description || '—'}</TableCell>
                <TableCell align="center">
                  <Tooltip title={r.active ? 'פעיל — לחץ להשבית' : 'לא פעיל — לחץ להפעיל'}>
                    <Switch size="small" checked={!!r.active} onChange={() => toggleActive(r)}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: NAVY } }} />
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => redirect('edit', 'matching_rules', r.id)} aria-label="עריכת כלל" sx={{ color: NAVY, opacity: 0.7 }}>
                    <EditIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(r.id, r.description)} aria-label="מחיקת כלל" sx={{ color: '#c62828' }}>
                    <DeleteIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
          {data.length === 0 && (
            <TableRow><TableCell colSpan={9} align="center" sx={{ py: 5, color: 'text.secondary' }}>אין כללים בסט זה</TableCell></TableRow>
          )}
        </TableBody>
      </Table></Box>
    </TableContainer>
    <FitPagination page={page} setPage={setPage} total={data.length} perPage={fitCount} />
    </>
  );
};

const MatchingRulesList = () => {
  const notify = useNotify();
  const [ruleSets, setRuleSets]         = useState([]);
  const [setsLoading, setSetsLoading]   = useState(true);
  const [selectedSet, setSelectedSet]   = useState(null);
  const [addSetOpen, setAddSetOpen]     = useState(false);

  const loadRuleSets = useCallback((keepSelectionId) => {
    setSetsLoading(true);
    authFetch('/matching_rule_sets?_start=0&_end=100&_sort=id&_order=ASC')
      .then(r => r.json())
      .then(d => {
        const rows = Array.isArray(d) ? d : [];
        setRuleSets(rows);
        setSelectedSet(prev => {
          const wantId = keepSelectionId ?? prev?.id;
          return rows.find(s => s.id === wantId) || rows[0] || null;
        });
      })
      .catch(() => notify('שגיאה בטעינת סטי הכללים', { type: 'error' }))
      .finally(() => setSetsLoading(false));
  }, [notify]);

  useEffect(() => { loadRuleSets(); }, [loadRuleSets]);

  return (
    <Box className="crm-page" p={2}>
      <Typography variant="body2" color="text.secondary" mb={2}>
        כללי ההתאמה מוגדרים בתוך "סט" — כל סט קושר מוצר מקור למוצר יעד (למשל תפילין ← בתי תפילין).
        בעת ייצור, וריאנט ממוצר המקור יותאם אוטומטית לוריאנטים של מוצר היעד לפי הכללים כאן.
      </Typography>

      <Stack className="crm-wrap crm-filter-row" direction="row" spacing={1.5} alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Autocomplete
          options={ruleSets} loading={setsLoading} value={selectedSet}
          onChange={(_, v) => setSelectedSet(v)}
          getOptionLabel={o => o ? `${o.name} (${o.source_product_name} ← ${o.target_product_name})` : ''}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          sx={{ minWidth: 340 }}
          renderInput={p => <TextField {...p} label="סט כללים" sx={inputSx} />}
        />
        <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => setAddSetOpen(true)}
          sx={{ borderColor: NAVY, color: NAVY, borderRadius: 2 }}>
          סט חדש
        </Button>
      </Stack>

      {!setsLoading && ruleSets.length === 0 && (
        <Typography color="text.secondary" py={4} textAlign="center">
          עדיין אין סטי כללי התאמה — לחצו "סט חדש" כדי להתחיל.
        </Typography>
      )}

      {selectedSet && (
        <List
          resource="matching_rules"
          filter={{ rule_set_id: selectedSet.id }}
          sort={{ field: 'id', order: 'ASC' }}
          perPage={1000}
          pagination={false}
          storeKey={false}
          actions={<Toolbar />}
          title=" "
          empty={false}
          sx={tbl.listSx}
        >
          <RuleTable />
        </List>
      )}

      <AddRuleSetDialog
        open={addSetOpen}
        onClose={() => setAddSetOpen(false)}
        onAdded={newSet => loadRuleSets(newSet.id)}
      />
    </Box>
  );
};

export default MatchingRulesList;
